import { useEffect, useMemo, useState } from "react";
import { api } from "./api/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const formatMoney = (val) => {
  const num = Number(val || 0);
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

export default function TabWarehouseFinancials({ dateFrom, dateTo }) {
  const [status, setStatus] = useState("IN"); // 'IN' (In Warehouse), 'OUT' (Delivered), 'ALL'
  const [operator, setOperator] = useState("");
  const [flightNo, setFlightNo] = useState("");
  const [manifestNo, setManifestNo] = useState("");
  const [currency, setCurrency] = useState("USD"); // 'USD' or 'SDG'

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({
    total_receipt_usd: 0,
    total_storage_usd: 0,
    grand_total_usd: 0,
    total_receipt_sdg: 0,
    total_storage_sdg: 0,
    grand_total_sdg: 0,
    count: 0
  });
  const [filterOptions, setFilterOptions] = useState({ operators: [], flights: [], manifests: [] });
  const [exchangeRate, setExchangeRate] = useState(2500);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const loadReport = async () => {
    try {
      setLoading(true);
      setMsg("");
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (operator) params.set("operator", operator);
      if (flightNo) params.set("flight_no", flightNo);
      if (manifestNo) params.set("manifest_no", manifestNo);
      if (dateFrom) params.set("from", dateFrom);
      if (dateTo) params.set("to", dateTo);

      const res = await api.get(`/reports/warehouse-financials?${params.toString()}`);
      setData(res?.data || []);
      setSummary(res?.summary || {});
      setFilterOptions(res?.filter_options || { operators: [], flights: [], manifests: [] });
      if (res?.exchange_rate) setExchangeRate(res.exchange_rate);

      if (!res?.data?.length) {
        setMsg("No records found for the selected filters.");
      }
    } catch (err) {
      console.error("Error loading warehouse financials report:", err);
      setMsg("Failed to load financial report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [status, dateFrom, dateTo]);

  const isSdg = currency === "SDG";
  const currencySymbol = isSdg ? "SDG " : "$ ";

  const getReceiptVal = (row) => (isSdg ? row.receipt_amount_sdg : row.receipt_amount_usd);
  const getStorageVal = (row) => (isSdg ? row.storage_amount_sdg : row.storage_amount_usd);
  const getTotalVal = (row) => (isSdg ? row.total_amount_sdg : row.total_amount_usd);

  const totalReceipt = isSdg ? summary.total_receipt_sdg : summary.total_receipt_usd;
  const totalStorage = isSdg ? summary.total_storage_sdg : summary.total_storage_usd;
  const grandTotal = isSdg ? summary.grand_total_sdg : summary.grand_total_usd;

  const groupedRows = useMemo(() => {
    const housesByMasterRef = new Map();
    const houses = [];

    data.forEach((row, idx) => {
      if (Number(row?.is_house || 0) !== 1) return;
      const groupKey = `${row?.manifest_bol_id || row?.bol_reference || "house"}-${idx}`;
      const normalizedRow = { ...row, __groupKey: groupKey };
      houses.push(normalizedRow);

      const masterRef = String(row?.master_bol_ref || "").trim();
      if (!masterRef) return;
      if (!housesByMasterRef.has(masterRef)) housesByMasterRef.set(masterRef, []);
      housesByMasterRef.get(masterRef).push(normalizedRow);
    });

    const consumedHouses = new Set();
    const result = [];

    data.forEach((row) => {
      if (Number(row?.is_house || 0) === 1) return;

      result.push({ kind: "master", row });

      const masterRef = String(row?.bol_reference || "").trim();
      const linkedHouses = masterRef ? (housesByMasterRef.get(masterRef) || []) : [];
      if (linkedHouses.length === 0) return;

      result.push({
        kind: "house_group_label",
        masterRef,
        count: linkedHouses.length,
      });

      linkedHouses.forEach((house) => {
        consumedHouses.add(house.__groupKey);
        result.push({ kind: "house", row: house, masterRef });
      });
    });

    houses.forEach((house) => {
      if (consumedHouses.has(house.__groupKey)) return;
      result.push({ kind: "house_orphan", row: house, masterRef: String(house?.master_bol_ref || "").trim() });
    });

    return result;
  }, [data]);

  const getDisplayAmount = (row, valueGetter) => {
    if (Number(row?.is_house || 0) === 1) return "—";
    return `${currencySymbol}${formatMoney(valueGetter(row))}`;
  };

  const handleExportPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
    const pageW = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.text(`Warehouse Financial Report (${status === "IN" ? "In Warehouse" : status === "OUT" ? "Delivered OUT" : "All AWBs"})`, pageW / 2, 35, { align: "center" });

    doc.setFontSize(10);
    doc.text(`Currency: ${currency} | Rate: 1 USD = ${exchangeRate} SDG | Total AWBs: ${summary.count || 0}`, pageW / 2, 52, { align: "center" });

    const operatorLabel = operator?.trim() ? operator.trim() : "ALL";
    const flightLabel = flightNo?.trim() ? flightNo.trim() : "ALL";
    const manifestLabel = manifestNo?.trim() ? manifestNo.trim() : "ALL";
    doc.setFontSize(9);
    doc.text(`Filters -> Operator: ${operatorLabel} | Flight: ${flightLabel} | Manifest: ${manifestLabel}`, pageW / 2, 66, { align: "center" });

    const headers = [["#", "AWB Reference", "Type", "Operator", "Flight No", "Manifest No", `Receipt (${currency})`, `Storage (${currency})`, `Total (${currency})`, "Status"]];
    const printableRows = groupedRows.filter((entry) => entry.kind !== "house_group_label");
    const rows = printableRows.map((entry, idx) => {
      const item = entry.row;
      const isHouse = Number(item?.is_house || 0) === 1;

      return [
        idx + 1,
        `${isHouse ? "  - " : ""}${item.bol_reference || ""}`,
        isHouse ? "House" : "Master",
        item.operator_name || "-",
        item.flight_no || "-",
        item.manifest_no || "-",
        isHouse ? "-" : formatMoney(getReceiptVal(item)),
        isHouse ? "-" : formatMoney(getStorageVal(item)),
        isHouse ? "-" : formatMoney(getTotalVal(item)),
        item.is_delivered ? `OUT (${item.delivery_date ? String(item.delivery_date).slice(0, 10) : ""})` : "IN Stock"
      ];
    });

    autoTable(doc, {
      startY: 78,
      head: headers,
      body: rows,
      theme: "striped",
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [25, 118, 210], textColor: [255, 255, 255], fontStyle: "bold" },
      foot: [[
        "TOTAL", "", "", "", "", "",
        formatMoney(totalReceipt),
        formatMoney(totalStorage),
        formatMoney(grandTotal),
        ""
      ]],
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: "bold" }
    });

    doc.save(`Warehouse_Financial_Report_${status}_${currency}.pdf`);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", flexWrap: "wrap", gap: "10px" }}>
        <h3 style={{ margin: 0, color: "#1565c0" }}>
          Warehouse Financial Report ({status === "IN" ? "In Warehouse / Stock" : status === "OUT" ? "Delivered OUT" : "All AWBs"})
        </h3>

        {/* Currency Switcher */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", background: "#e3f2fd", padding: "6px 14px", borderRadius: "6px", border: "1px solid #90caf9" }}>
          <span style={{ fontWeight: "bold", color: "#1565c0", fontSize: "14px" }}>Currency:</span>
          <label style={{ cursor: "pointer", fontWeight: currency === "USD" ? "bold" : "normal", color: "#0d47a1", display: "flex", alignItems: "center", gap: "4px", margin: 0 }}>
            <input type="radio" name="reportCurrency" value="USD" checked={currency === "USD"} onChange={() => setCurrency("USD")} />
            USD ($)
          </label>
          <label style={{ cursor: "pointer", fontWeight: currency === "SDG" ? "bold" : "normal", color: "#0d47a1", display: "flex", alignItems: "center", gap: "4px", margin: 0 }}>
            <input type="radio" name="reportCurrency" value="SDG" checked={currency === "SDG"} onChange={() => setCurrency("SDG")} />
            SDG (Rate: {exchangeRate})
          </label>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card" style={{ background: "#f8f9fa", padding: "15px", marginBottom: "20px", borderRadius: "8px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", alignItems: "end" }}>
          <div>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: "4px", fontSize: "13px" }}>Warehouse Status:</label>
            <select className="edit-input" style={{ width: "100%", padding: "8px" }} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="IN">In Warehouse </option>
              <option value="OUT">Delivered ( OUT )</option>
              <option value="ALL">All AWBs </option>
            </select>
          </div>

          <div>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: "4px", fontSize: "13px" }}>Operator:</label>
            <input
              className="edit-input"
              style={{ width: "100%", padding: "8px" }}
              placeholder="Search or select operator..."
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
              list="operator-options"
            />
            <datalist id="operator-options">
              {filterOptions.operators.map((op, i) => (
                <option key={i} value={op} />
              ))}
            </datalist>
          </div>

          <div>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: "4px", fontSize: "13px" }}>Flight No:</label>
            <input
              className="edit-input"
              style={{ width: "100%", padding: "8px" }}
              placeholder="Search or select flight..."
              value={flightNo}
              onChange={(e) => setFlightNo(e.target.value)}
              list="flight-options"
            />
            <datalist id="flight-options">
              {filterOptions.flights.map((fl, i) => (
                <option key={i} value={fl} />
              ))}
            </datalist>
          </div>

          <div>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: "4px", fontSize: "13px" }}>Manifest No:</label>
            <input
              className="edit-input"
              style={{ width: "100%", padding: "8px" }}
              placeholder="Manifest / D_No..."
              value={manifestNo}
              onChange={(e) => setManifestNo(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <button className="btn btn-primary" style={{ padding: "8px 16px", flex: 1 }} onClick={loadReport} disabled={loading}>
              {loading ? "Loading..." : "Filter / Refresh"}
            </button>
            <button className="btn btn-secondary" style={{ padding: "8px 16px" }} onClick={handleExportPdf} disabled={!data.length}>
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {msg && <div className="message info" style={{ marginBottom: "15px" }}>{msg}</div>}

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "20px" }}>
        <div style={{ background: "#e8f5e9", borderLeft: "5px solid #2e7d32", padding: "14px", borderRadius: "8px" }}>
          <div style={{ color: "#2e7d32", fontSize: "12px", fontWeight: "bold" }}>TOTAL RECEIPT AMOUNT</div>
          <div style={{ fontSize: "20px", fontWeight: "bold", marginTop: "4px" }}>
            {currencySymbol}{formatMoney(totalReceipt)}
          </div>
          <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>Service + Packing + Build-up</div>
        </div>

        <div style={{ background: "#e3f2fd", borderLeft: "5px solid #1565c0", padding: "14px", borderRadius: "8px" }}>
          <div style={{ color: "#1565c0", fontSize: "12px", fontWeight: "bold" }}>TOTAL STORAGE INVOICE</div>
          <div style={{ fontSize: "20px", fontWeight: "bold", marginTop: "4px" }}>
            {currencySymbol}{formatMoney(totalStorage)}
          </div>
          <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>Cargo Storage Fees</div>
        </div>

        <div style={{ background: "#fff3e0", borderLeft: "5px solid #ef6c00", padding: "14px", borderRadius: "8px" }}>
          <div style={{ color: "#ef6c00", fontSize: "12px", fontWeight: "bold" }}>GRAND TOTAL</div>
          <div style={{ fontSize: "20px", fontWeight: "bold", marginTop: "4px" }}>
            {currencySymbol}{formatMoney(grandTotal)}
          </div>
          <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>Receipt + Storage Total</div>
        </div>

        <div style={{ background: "#f3e5f5", borderLeft: "5px solid #7b1fa2", padding: "14px", borderRadius: "8px" }}>
          <div style={{ color: "#7b1fa2", fontSize: "12px", fontWeight: "bold" }}>TOTAL RECORD COUNT</div>
          <div style={{ fontSize: "20px", fontWeight: "bold", marginTop: "4px" }}>
            {summary.count || 0} AWBs
          </div>
          <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>Matched AWBs</div>
        </div>
      </div>

      {/* Data Table */}
      <div style={{ overflowX: "auto" }}>
        <table className="grid-table">
          <thead>
            <tr>
              <th>#</th>
              <th>AWB Reference</th>
              <th>Type</th>
              <th>Operator</th>
              <th>Flight No</th>
              <th>Manifest No</th>
              <th>Receipt Amount ({currency})</th>
              <th>Storage Invoice ({currency})</th>
              <th>Total Amount ({currency})</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: "center", padding: "20px", color: "#777" }}>
                  {loading ? "Loading financial data..." : "No records found matching criteria."}
                </td>
              </tr>
            ) : (
              groupedRows.map((entry, idx) => {
                if (entry.kind === "house_group_label") {
                  return (
                    <tr key={`group-${entry.masterRef}-${idx}`} style={{ background: "#f8fbff" }}>
                      <td colSpan="10" style={{
                        padding: "9px 12px",
                        borderLeft: "6px solid #42a5f5",
                        color: "#0d47a1",
                        fontWeight: "bold",
                        fontSize: "12px"
                      }}>
                        Houses Linked To Master {entry.masterRef} ({entry.count})
                      </td>
                    </tr>
                  );
                }

                const row = entry.row;
                const isHouse = Number(row?.is_house || 0) === 1;
                const rowKey = `${entry.kind}-${row?.manifest_bol_id || row?.bol_reference || idx}`;

                return (
                  <tr
                    key={rowKey}
                    style={isHouse
                      ? { background: "#f7fbff", borderLeft: "6px solid #90caf9" }
                      : { background: "#eef6ff", borderTop: "2px solid #90caf9", borderBottom: "1px solid #d6eaff" }}
                  >
                    <td>{idx + 1}</td>
                    <td style={{ fontWeight: "bold", color: isHouse ? "#1565c0" : "#0d47a1", paddingLeft: isHouse ? "24px" : undefined }}>
                      {isHouse ? "↳ " : ""}{row.bol_reference}
                    </td>
                    <td>{isHouse ? <span className="badge badge-info">House</span> : <span className="badge badge-primary">Master</span>}</td>
                    <td>{row.operator_name || "-"}</td>
                    <td>{row.flight_no || "-"}</td>
                    <td>{row.manifest_no || "-"}</td>
                    <td style={{ color: "#2e7d32", fontWeight: "bold" }}>{getDisplayAmount(row, getReceiptVal)}</td>
                    <td style={{ color: "#1565c0", fontWeight: "bold" }}>{getDisplayAmount(row, getStorageVal)}</td>
                    <td style={{ color: "#ef6c00", fontWeight: "bold" }}>{getDisplayAmount(row, getTotalVal)}</td>
                    <td>
                      {row.is_delivered ? (
                        <span className="badge badge-success">OUT ({row.delivery_date ? String(row.delivery_date).slice(0, 10) : "Delivered"})</span>
                      ) : (
                        <span className="badge badge-warning">IN Stock</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {data.length > 0 && (
            <tfoot>
              <tr style={{ background: "#f5f5f5", fontWeight: "bold" }}>
                <td colSpan="6" style={{ textAlign: "right", padding: "10px" }}>Overall Totals ({currency}):</td>
                <td style={{ color: "#2e7d32", fontSize: "14px" }}>{currencySymbol}{formatMoney(totalReceipt)}</td>
                <td style={{ color: "#1565c0", fontSize: "14px" }}>{currencySymbol}{formatMoney(totalStorage)}</td>
                <td style={{ color: "#d32f2f", fontSize: "15px" }}>{currencySymbol}{formatMoney(grandTotal)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
